import React, {
  useState,
  useRef,
  useEffect,
  createContext,
  useContext,
} from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import "./style.css";
import Popup from "../components/popup.js";
import { Helmet } from "react-helmet-async";
import useFetch from "../hooks/useFetch.js";
import Lottie from "react-lottie";
import loading from "../pages/loading.json";
import Sidebar, { SearchContext } from "../pages/SidebarAcademy.js";
import Header from "../pages/HeaderAcademy.js";

function titleToSlug(title) {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const ShortCourse = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [isFromSearch, setIsFromSearch] = useState(false);

  // Create a ref for the main content area
  const mainContentRef = useRef(null);

  const {
    loading: academyLoading,
    data: academyData,
    error: academyError,
  } = useFetch(
    `https://devrw2.blob.core.windows.net/rw-website/strapi-data/academy/data.json`
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get("search");
    const fromSearch = urlParams.get("fromSearch");

    if (searchParam) {
      setSearchTerm(searchParam);
      setIsFromSearch(fromSearch === "true");
    }
  }, [location.search]);

  useEffect(() => {
    if (searchTerm.trim()) {
      const newUrl = `${location.pathname}?search=${encodeURIComponent(
        searchTerm
      )}${isFromSearch ? "&fromSearch=true" : ""}`;
      window.history.replaceState(null, "", newUrl);
    } else {
      window.history.replaceState(null, "", location.pathname);
      setIsFromSearch(false);
    }
  }, [searchTerm, location.pathname, isFromSearch]);

  // Auto-scroll to main content when component starts functioning (data is loaded)
  // useEffect(() => {
  //   if (!academyLoading && academyData && mainContentRef.current) {
  //     // Small delay to ensure the component is fully rendered
  //     const timer = setTimeout(() => {
  //       mainContentRef.current.scrollIntoView({
  //         behavior: "smooth",
  //         block: "start" // This will align to the top of the element
  //       });
  //     }, 300);

  //     return () => clearTimeout(timer);
  //   }
  // }, [academyLoading, academyData]);

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: loading,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  if (academyLoading) {
    return (
      <div className="w-full flex justify-center items-center min-h-screen">
        <Lottie options={defaultOptions} height={400} width={400} />
      </div>
    );
  }

  const academyItems = Array.isArray(academyData) ? academyData : [];

  const filteredCourses = academyItems.filter((academy) => {
    if (academy.academyCategory !== "Bite-sized Courses") return false;

    if (!searchTerm.trim()) return true;

    if (isFromSearch && searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      return academy.academyTitle?.toLowerCase().includes(searchLower);
    }

    const searchLower = searchTerm.toLowerCase();
    return (
      academy.academyTitle?.toLowerCase().includes(searchLower) ||
      academy.academyDesc?.toLowerCase().includes(searchLower) ||
      academy.academyCategory?.toLowerCase().includes(searchLower)
    );
  });

  const coursesToShow =
    isFromSearch && filteredCourses.length > 0
      ? [filteredCourses[0]]
      : filteredCourses;

  const handleClearSearch = () => {
    setSearchTerm("");
    setIsFromSearch(false);

    window.history.replaceState(null, "", location.pathname);
  };

  return (
    <SearchContext.Provider value={{ searchTerm, setSearchTerm }}>
      <div className="bg-gray-50 font-display">
        <Header />
        {/* Add ref to the main content div */}
        <div
          ref={mainContentRef}
          className="px-4 py-8 flex flex-col md:flex-row"
        >
          <Sidebar searchContextValue={{ searchTerm, setSearchTerm }} />
          <div className="md:w-4/5 2xl:w-full">
            <div className="p-4 mb-8">
              <div className="mb-2">
                <h2 className="text-2xl font-bold text-left text-black">
                  Digital Skills Courses (57)
                </h2>
              </div>
              <div className="mb-4 flex justify-between items-center">
                <h2 className="text-lg font-medium text-left text-gray-900">
                  Bite-sized Courses ({coursesToShow.length})
                </h2>
              </div>

              {coursesToShow.length === 0 && searchTerm ? (
                <div className="text-center py-12">
                  <div className="text-gray-500 text-lg mb-2">
                    No courses found
                  </div>
                  <div className="text-gray-400 text-sm">
                    Try adjusting your search terms or{" "}
                    <button
                      onClick={handleClearSearch}
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      view all courses
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 mt-3">
                  {coursesToShow.map((academy) => (
                    <BlogCard key={academy.id} academy={academy} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <section className="mt-20">
          <Popup utmContent={"blog"} />
        </section>
      </div>
    </SearchContext.Provider>
  );
};

const BlogCard = ({ academy }) => {
  const titleRef = useRef();
  const [titleLines, setTitleLines] = useState(1);
  const { searchTerm } = useContext(SearchContext);

  useEffect(() => {
    if (titleRef.current) {
      const lineHeight = parseInt(
        window.getComputedStyle(titleRef.current).lineHeight
      );
      const titleHeight = titleRef.current.clientHeight;
      const lines = Math.ceil(titleHeight / lineHeight);
      setTitleLines(lines);
    }
  }, [titleRef.current]);

  const descriptionLines = 7 - titleLines;

  const highlightText = (text, searchTerm) => {
    if (!searchTerm || !text) return text;

    const regex = new RegExp(`(${searchTerm})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="bg-yellow-200 font-medium">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <div className="bg-[#ECEFF8] overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow duration-300 flex flex-col h-full rounded-3xl">
      <div className="relative w-full h-48 overflow-hidden">
        <img
          className="object-cover w-full h-full"
          src={academy.academycoverImg.url}
          alt={academy.academyTitle}
        />
        <div className="absolute top-2 right-2 bg-[rgba(62,89,183,1)] text-white px-3 py-1 font-bold rounded-md">
          ${academy.academyPrice}
        </div>
      </div>
      <div className="p-4 flex flex-col flex-grow">
        <h3 ref={titleRef} className="text-lg font-bold text-gray-800 mb-2">
          {highlightText(academy.academyTitle, searchTerm)}
        </h3>
        <p
          className="text-gray-600 text-sm mb-4"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: descriptionLines,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {highlightText(academy.academyDesc, searchTerm)}
        </p>
        <div className="flex justify-center mt-auto">
          <Link
            to={`/academy/bite-sized-courses/${titleToSlug(
              academy.academyTitle
            )}/`}
            className="bg-[rgba(62,89,183,1)] text-white px-24 md:px-10 lg:px-20 py-2 rounded-lg hover:bg-blue-700 transition text-nowrap"
          >
            Learn More
          </Link>
        </div>
      </div>
    </div>
  );
};

export { SearchContext };
export default ShortCourse;
